import React, {useCallback} from 'react';

import {zodResolver} from '@hookform/resolvers/zod';
import classNames from 'classnames';
import {FieldErrors, FormProvider, useForm} from 'react-hook-form';
import {connect} from 'react-redux';

import {useFeature} from 'components/feature';
import {ApplicationStoreType} from 'desktop/store';
import {AddressFormType} from 'desktop/types/addressType';

import {flags} from 'libs/rollout';

import {defaultAddress} from '../../constants/deliveryStep';
import {finishDeliveryStep} from '../../store/reducers/sagaStep/sagaStep';
import {
    getAddressesSameness,
    isDeliveryStepLoaded,
} from '../../store/selectors/deliveryStep/deliveryStep';
import {getIsCartHasLuxuryItem} from '../../store/selectors/orderReviewItems/orderReviewItems';
import {useDeliveryFormValidationSchema} from '../../utils/deliveryFormValidation/deliveryFormValidation';
import {handleSubscriptionAnalytics} from '../../utils/newsletterSubscription/analytics';
import {useSubscriptionsMethod} from '../../utils/newsletterSubscription/useSubscriptionMethods';
import {CaliforniaPrepositionNoticeConnected} from '../californiaPropositionNotice/californiaPrepositionNotice';
import {DeliveryContentWrapperConnected} from '../deliveryContentWrapper/deliveryContentWrapper';
import {DeliveryFormConnected} from '../deliveryForm/deliveryForm';
import {DeliveryFormCheckboxConnected} from '../deliveryFormCheckbox/deliveryFormCheckbox';
import {DeliveryFormContinueButtonConnected} from '../deliveryFormContinueButton/deliveryFormContinueButton';
import {DeliveryFormForUserWithAddressConnected} from '../deliveryFormForUserWithAddress/deliveryFormForUserWithAddress';
import {ShippingProtectionWrapper} from '../shippingProtection/shippingProtectionWrapper';

import {FormFromReduxUpdaterConnected} from './formFromReduxUpdater';
import {useDeliveryFormState} from './useDeliveryFormState';

import {useOpenPaymentStepIfValidToken} from './useOpenPaymentStepIfValidToken';

import s from './deliveryFormWrapper.scss';

export type PropsType = {
    changeStepToPayment(): void;
    isLoaded: boolean;
    isUserWithAddress: boolean;
    useThisAddressForBothAddresses: boolean;
    hasLuxuryItem: boolean;
};

/**
 * Delivery form wrapper which renders DeliveryForm or DeliveryFormForUserWithAddress
 */
export const DeliveryFormWrapper = ({
    changeStepToPayment,
    hasLuxuryItem,
    isLoaded,
    isUserWithAddress,
    useThisAddressForBothAddresses,
}: PropsType) => {
    const isFacelift = useFeature('dCheckoutFacelift');

    const {
        isCartHasActiveSubscriptionCL,
        isCartHasGlassesCare,
        isOneStepAvailable,
        isSubscriptionPopupEnabled,
        setSubscriptionPopupStatus,
    } = useDeliveryFormState();

    const schema = useDeliveryFormValidationSchema();
    const methods = useForm<AddressFormType>({
        defaultValues: {
            billingAddress: defaultAddress,
            shippingAddress: defaultAddress,
        },
        mode: 'onTouched',
        resolver: zodResolver(schema),
    });
    const handleSubscription = useSubscriptionsMethod(methods.getValues, hasLuxuryItem);

    const goToPaymentStep = useCallback(() => {
        handleSubscription();
        changeStepToPayment();
    }, [changeStepToPayment, handleSubscription]);

    const onInvalidSubmit = (errors: FieldErrors<AddressFormType>) => {
        const invalidFields = Object.keys({
            ...errors.shippingAddress,
            ...(useThisAddressForBothAddresses ? {} : errors.billingAddress),
        });

        if (invalidFields.length === 0) {
            goToPaymentStep();
            return;
        }
        handleSubscriptionAnalytics(`Error - Required Fields Missing - ${invalidFields.join('|')}`);
    };

    useOpenPaymentStepIfValidToken(
        {clearErrors: methods.clearErrors, trigger: methods.trigger},
        goToPaymentStep,
    );

    const onValidSubmit = () => {
        const shouldShowPopup =
            (isCartHasActiveSubscriptionCL && flags.checkoutCLConsent.isEnabled()) ||
            isCartHasGlassesCare;

        if (shouldShowPopup) {
            setSubscriptionPopupStatus(true);
        } else {
            goToPaymentStep();
        }
    };

    return (
        <div
            className={classNames(s.wrapper, {
                [s.oneStep]: isOneStepAvailable,
                [s.wrapperFacelift]: isFacelift,
            })}
        >
            <FormProvider {...methods}>
                <form
                    name="CheckoutDeliveryForm"
                    onSubmit={methods.handleSubmit(onValidSubmit, onInvalidSubmit)}
                >
                    <fieldset disabled={!isLoaded}>
                        <div className={classNames(s.carrier, {[s.oneStep]: isOneStepAvailable})}>
                            {isUserWithAddress ? (
                                <DeliveryFormForUserWithAddressConnected />
                            ) : (
                                <DeliveryFormConnected />
                            )}

                            <CaliforniaPrepositionNoticeConnected />
                            {!isOneStepAvailable && <DeliveryFormCheckboxConnected />}
                            <DeliveryContentWrapperConnected />
                            {!isOneStepAvailable && <ShippingProtectionWrapper />}
                        </div>
                        {!isOneStepAvailable && (
                            <DeliveryFormContinueButtonConnected
                                handleSubmit={methods.handleSubmit(
                                    goToPaymentStep,
                                    onInvalidSubmit,
                                )}
                                closeSubscriptionPopup={() => setSubscriptionPopupStatus(false)}
                                isSubscriptionPopupEnabled={isSubscriptionPopupEnabled}
                            />
                        )}
                    </fieldset>
                </form>

                {/* This component updated react-hook-form state from redux, see cases described in the component */}
                <FormFromReduxUpdaterConnected />
            </FormProvider>
        </div>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    hasLuxuryItem: getIsCartHasLuxuryItem(store),
    isLoaded: isDeliveryStepLoaded(store),
    useThisAddressForBothAddresses: getAddressesSameness(store),
});

export const DeliveryFormWrapperConnected = connect(mapStateToProps, {
    changeStepToPayment: finishDeliveryStep,
})(DeliveryFormWrapper);
